// The Recruiter's prepared fields (Draft or Revision Required only): row 1 locked from the Finalized review, rows 2
// and 3 as inputs, the start date, Save (comp.propose) and the Draft -> Pending Approvals control. His rule that a
// package change invalidates the affected approvals is the form's hint.
import { useState } from "react";
import { ACTION_LABELS, Button, Card, COMMON_LABELS, COMPENSATION_REVIEW_STATUS, Field, Flex, Input, KeyValue, Stack } from "@/mc";
import type { CompOut, PrepareValues } from "./CompensationTab";
import { PKG_FIELD_LABELS, PKG_SEND_LABELS, PKG_ROW_LABELS, PKG_RULES } from "./labels";
import { money } from "./PackageCard";

const str = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

export interface PrepareFormProps {
  comp: CompOut;
  /** The package exists and the server allows Draft -> Pending Approvals. */
  canSend: boolean;
  onSave?: ((values: PrepareValues) => Promise<unknown>) | undefined;
  onSend?: (() => Promise<unknown>) | undefined;
}

export function PrepareForm({ comp, canSend, onSave, onSend }: PrepareFormProps) {
  const pkg = comp.package ?? null;
  const [values, setValues] = useState<PrepareValues>({
    base: str(pkg?.base), bonus_target: str(pkg?.bonus_target), commission: str(pkg?.commission), sign_on: str(pkg?.sign_on),
    relocation: str(pkg?.relocation), lti: str(pkg?.lti), lti_wording: str(pkg?.lti_wording), start_date: str(pkg?.start_date),
  });
  const [busy, setBusy] = useState<"save" | "send" | null>(null);
  const set = (key: keyof PrepareValues) => (event: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: event.target.value }));
  const missing = values.base.trim() === "" ? "base" : values.start_date.trim() === "" ? "start_date" : null;
  const run = async (which: "save" | "send", fn: (() => Promise<unknown>) | undefined) => {
    // one write at a time: Submit sends the version the worksheet shows, so it waits for a Save to be read back
    if (!fn || busy) return;
    setBusy(which);
    try { await fn(); } finally { setBusy(null); }
  };
  const reco = comp.recommendation;
  const r = reco?.range;
  const row1 = [reco?.text, r ? `${money(r.min, r.currency)} – ${money(r.max, r.currency)}` : ""].filter(Boolean).join(" · ") || "—";
  const amount = (key: keyof PrepareValues, label: string, required = false) => (
    <Field label={label} required={required} optional={!required}>
      <Input inputMode="decimal" value={values[key]} onChange={set(key)} hs={key === "base" ? "offer.pkg.prepare" : undefined} />
    </Field>
  );
  return (
    <Card title={ACTION_LABELS.prepare} icon="note">
      <Stack>
        <KeyValue items={[[PKG_ROW_LABELS.row1, row1, COMPENSATION_REVIEW_STATUS.finalized, 3]]} cols={3} />
        <fieldset className="mc-pkg-fs">
          <legend>{PKG_ROW_LABELS.row2}</legend>
          <Flex wrap gap={12}>
            {amount("base", PKG_FIELD_LABELS.proposedBase, true)}
            {amount("bonus_target", PKG_FIELD_LABELS.bonus)}
            {amount("commission", PKG_FIELD_LABELS.commission)}
          </Flex>
        </fieldset>
        <fieldset className="mc-pkg-fs">
          <legend>{PKG_ROW_LABELS.row3}</legend>
          <Flex wrap gap={12}>
            {amount("sign_on", PKG_FIELD_LABELS.signOn)}
            {amount("relocation", PKG_FIELD_LABELS.relocation)}
            {amount("lti", PKG_FIELD_LABELS.lti)}
          </Flex>
          {values.lti.trim() !== "" ? (
            <Field label={PKG_FIELD_LABELS.wording} required hint={PKG_RULES.vpLti}>
              <Input value={values.lti_wording} onChange={set("lti_wording")} />
            </Field>
          ) : null}
        </fieldset>
        <Field label={PKG_FIELD_LABELS.startDate} required>
          <Input type="date" value={values.start_date} onChange={set("start_date")} />
        </Field>
        <p className="mc-hint">{PKG_RULES.invalidates}</p>
        <Flex gap={8} justify="end">
          <Button
            busy={busy !== null}
            blocked={missing ? { reason: COMMON_LABELS.required } : undefined}
            onClick={() => { void run("save", onSave && (() => onSave(values))); }}
            
          >
            {ACTION_LABELS.save}
          </Button>
          {canSend ? (
            <Button tone="primary" busy={busy !== null} onClick={() => { void run("send", onSend); }} >
              {PKG_SEND_LABELS.send}
            </Button>
          ) : null}
        </Flex>
      </Stack>
    </Card>
  );
}
